import { resolveFactFindCompleteAssignee } from './taskAssigneeHelpers';
import { firmRoleById } from './workflowFirmRoles';
import { getCurrentAdviser, isAdviserDerivedKey, CURRENT_ADVISER_LABEL } from './currentAdviser';

/** 'current_adviser' is the one Assigned Adviser; the onboarding / AR keys are legacy aliases (Sept 2026). */
export const TASK_ASSIGNEE_DERIVED_KEYS = {
  CURRENT_ADVISER: 'current_adviser',
  ONBOARDING_ADVISER: 'onboarding_adviser',
  AR_ADVISER: 'ar_adviser',
  FACT_FINDER: 'fact_finder',
  MARKETER: 'marketer',
};

/** Options the pickers offer; the legacy adviser keys still display as "Assigned Adviser". */
export const TASK_ASSIGNEE_DERIVED_OPTIONS = [
  { key: TASK_ASSIGNEE_DERIVED_KEYS.CURRENT_ADVISER, label: CURRENT_ADVISER_LABEL },
  { key: TASK_ASSIGNEE_DERIVED_KEYS.FACT_FINDER, label: 'Fact Finder' },
  { key: TASK_ASSIGNEE_DERIVED_KEYS.MARKETER, label: 'Assigned Marketer' },
];

export function normAssigneeEmail(email) {
  return typeof email === 'string' ? email.trim().toLowerCase() : '';
}

export function resolveDerivedTaskAssignee(derivedKey, clientData) {
  if (isAdviserDerivedKey(derivedKey)) {
    const { email, name } = getCurrentAdviser(clientData);
    return { assignedTo: email, assignedToName: name };
  }
  if (derivedKey === TASK_ASSIGNEE_DERIVED_KEYS.FACT_FINDER) {
    const { email, name } = resolveFactFindCompleteAssignee(clientData);
    return { assignedTo: email || '', assignedToName: name || '' };
  }
  if (derivedKey === TASK_ASSIGNEE_DERIVED_KEYS.MARKETER) {
    const assignment = clientData?.assignment || {};
    return { assignedTo: normAssigneeEmail(assignment.assignedMarketer), assignedToName: String(assignment.assignedMarketerName || '').trim() };
  }
  return { assignedTo: '', assignedToName: '' };
}

/**
 * Built-in defaults applied when Admin has not configured an entry for the
 * action (owner decision 2026-08-22: AR implementation actions go to the
 * client's AR adviser).
 */
export const BUILT_IN_TASK_ASSIGNEE_DEFAULTS = {
  'investment-switch-fund': { type: 'derived', derivedKey: TASK_ASSIGNEE_DERIVED_KEYS.CURRENT_ADVISER },
  'ongoing-fee-consent-renewed': { type: 'derived', derivedKey: TASK_ASSIGNEE_DERIVED_KEYS.CURRENT_ADVISER },
  // Envelope approval mirrors Approve SOA: default to the assigned adviser.
  'send-envelope-approved': { type: 'derived', derivedKey: TASK_ASSIGNEE_DERIVED_KEYS.CURRENT_ADVISER },
};

/**
 * The advisory team (firm) a client belongs to — the key for the per-firm
 * default layer. Mirrors server/lib/advisoryTeam.cjs resolveAdvisoryTeamId.
 */
export function resolveTaskAssigneeAdvisoryTeamId(clientData) {
  const id = clientData?.assignment?.assignedAdvisory;
  const trimmed = String(id || '').trim();
  return trimmed || null;
}

export const TASK_ASSIGNEE_DEFAULT_SOURCES = {
  TEAM: 'team',
  GLOBAL: 'global',
  BUILT_IN: 'builtIn',
};

function isEntry(value) {
  return Boolean(value) && typeof value === 'object' && typeof value.type === 'string';
}

/**
 * Every layer that can supply a default for an action, plus the effective one.
 * Precedence: firm override → all-firms default → built-in.
 * Config shape: { defaults: { [actionId]: entry }, teams: { [advisoryTeamId]: { [actionId]: entry } } }
 */
export function getTaskAssigneeDefaultLayers(defaultsConfig, actionId, advisoryTeamId = null) {
  const teamId = String(advisoryTeamId || '').trim();
  const teamMap = teamId && defaultsConfig?.teams && typeof defaultsConfig.teams === 'object'
    ? defaultsConfig.teams[teamId]
    : null;
  const team = isEntry(teamMap?.[actionId]) ? teamMap[actionId] : null;
  const global = isEntry(defaultsConfig?.defaults?.[actionId]) ? defaultsConfig.defaults[actionId] : null;
  const builtIn = BUILT_IN_TASK_ASSIGNEE_DEFAULTS[actionId] || null;
  let effective = null;
  let source = null;
  if (team) {
    effective = team;
    source = TASK_ASSIGNEE_DEFAULT_SOURCES.TEAM;
  } else if (global) {
    effective = global;
    source = TASK_ASSIGNEE_DEFAULT_SOURCES.GLOBAL;
  } else if (builtIn) {
    effective = builtIn;
    source = TASK_ASSIGNEE_DEFAULT_SOURCES.BUILT_IN;
  }
  return { team, global, builtIn, effective, source, advisoryTeamId: teamId || null };
}

/** Resolve the effective default assignee config for a milestone actionId on this client. */
export function resolveFirmMilestoneAssignee(actionId, clientData, defaultsConfig) {
  const { effective: entry } = getTaskAssigneeDefaultLayers(
    defaultsConfig,
    actionId,
    resolveTaskAssigneeAdvisoryTeamId(clientData)
  );
  if (!entry) {
    return { assignedTo: '', assignedToName: '' };
  }

  if (entry.type === 'user') {
    return {
      assignedTo: (entry.email || '').trim(),
      assignedToName: (entry.name || '').trim(),
    };
  }

  if (entry.type === 'derived') {
    return resolveDerivedTaskAssignee(entry.derivedKey, clientData);
  }

  return { assignedTo: '', assignedToName: '' };
}

/**
 * Effective default entry for an action. Pass the client (or its advisory team
 * id) so a firm override wins over the all-firms default.
 */
export function getTaskAssigneeDefaultConfig(defaultsConfig, actionId, clientOrTeamId = null) {
  const advisoryTeamId = typeof clientOrTeamId === 'string'
    ? clientOrTeamId
    : resolveTaskAssigneeAdvisoryTeamId(clientOrTeamId);
  return getTaskAssigneeDefaultLayers(defaultsConfig, actionId, advisoryTeamId).effective;
}

/**
 * @param {object} [options]
 * @param {Array|object} [options.firmRoles]  the firm's workflow roles (list or byId); when supplied a
 *   firm-role label is resolved live (renames, "(unassigned)", "(deleted)"), else the stored name snapshot
 */
export function formatTaskAssigneeDefaultLabel(config, { firmRoles = null } = {}) {
  if (!config) return null;
  if (config.type === 'firmRole') {
    const snapshot = config.name || 'Workflow role';
    if (!firmRoles) return snapshot;
    const role = firmRoleById(firmRoles, config.firmRoleId);
    if (!role) return `${snapshot} (deleted)`;
    return role.email ? role.name : `${role.name} (unassigned)`;
  }
  if (config.type === 'derived') {
    if (isAdviserDerivedKey(config.derivedKey)) return CURRENT_ADVISER_LABEL;
    const opt = TASK_ASSIGNEE_DERIVED_OPTIONS.find((o) => o.key === config.derivedKey);
    return opt?.label || config.derivedKey;
  }
  if (config.type === 'user') {
    return config.name || config.email || null;
  }
  if (config.type === 'starter') return 'Workflow starter';
  return null;
}

/** True when current assignee matches the resolved firm default for this client. */
export function isAtFirmMilestoneAssigneeDefault(assignedTo, actionId, clientData, defaultsConfig) {
  const resolved = resolveFirmMilestoneAssignee(actionId, clientData, defaultsConfig);
  if (!resolved.assignedTo) {
    return !normAssigneeEmail(assignedTo);
  }
  return normAssigneeEmail(assignedTo) === normAssigneeEmail(resolved.assignedTo);
}

export function canResetToFirmMilestoneDefault(assignedTo, actionId, clientData, defaultsConfig) {
  const resolved = resolveFirmMilestoneAssignee(actionId, clientData, defaultsConfig);
  if (!resolved.assignedTo) return false;
  return !isAtFirmMilestoneAssigneeDefault(assignedTo, actionId, clientData, defaultsConfig);
}
